import { featureCategories, type Feature } from '@/data/features'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

function FeatureIcon({ path }: { path: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="w-7 h-7"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  )
}

function FeatureCard({ feature }: { feature: Feature }) {
  return (
    <div
      className="
        bg-gradient-to-br from-white/[0.03] to-white/[0.01]
        border border-white/[0.06] rounded-2xl p-8
        transition-all duration-400 relative overflow-hidden
        hover:-translate-y-1 hover:border-accent-teal/20
        hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]
        before:content-[''] before:absolute before:top-0 before:left-0 before:right-0
        before:h-0.5 before:bg-gradient-to-r before:from-transparent before:via-accent-teal before:to-transparent
        before:opacity-0 before:transition-opacity before:duration-400
        hover:before:opacity-100
        group
      "
    >
      <div
        className="
          w-14 h-14 rounded-xl bg-accent-teal/10 flex items-center justify-center
          text-accent-teal mb-5
          transition-all duration-300
          group-hover:bg-accent-teal/20 group-hover:scale-105
        "
      >
        <FeatureIcon path={feature.iconPath} />
      </div>
      <h3 className="font-serif text-xl font-semibold text-brand-light mb-3">{feature.title}</h3>
      <p className="text-[15px] text-brand-muted leading-relaxed">{feature.description}</p>
    </div>
  )
}

export default function FeaturesPage() {
  return (
    <>
      <SEO
        title="WealthSpace Features | AI Reports, Compliance & Client Portal"
        description="Explore WealthSpace features: AI report writing, meeting transcription, FCA compliance checking, client portal, and custom templates for UK financial advisers."
        keywords="AI report writing, meeting transcription software, FCA compliance automation, financial adviser client portal, suitability report generator, document templates IFA, adviser workflow automation, COBS compliance software"
        url="/features"
      />

      {/* Hero */}
      <section className="py-[100px] px-6 max-w-[1200px] mx-auto relative z-[1] text-center">
        <h1 className="font-serif text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6">
          Features built for <span className="text-accent-teal italic">modern</span> financial advice
        </h1>
        <p className="text-[20px] text-brand-muted leading-[1.7] font-normal max-w-[700px] mx-auto">
          Everything you need to streamline your advice workflow, ensure compliance, and deliver
          exceptional client outcomes.
        </p>
      </section>

      {/* Features Grid by Category */}
      <section className="px-6 pb-[100px] relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          {featureCategories.map((category) => (
            <div key={category.anchor} id={category.anchor} className="mb-20 last:mb-0">
              {/* Category Header */}
              <div className="mb-10">
                <h2 className="font-serif text-[32px] font-normal text-brand-light mb-3">
                  <span className="text-accent-teal text-4xl mr-2">"</span>
                  {category.category}
                </h2>
                <p className="text-lg text-brand-muted max-w-[600px]">{category.description}</p>
              </div>

              {/* Feature Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((feature, index) => (
                  <FeatureCard key={index} feature={feature} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-[100px] px-6 max-w-[900px] mx-auto text-center relative z-[1]"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%)',
        }}
      >
        <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-3xl py-16 px-12">
          <h2 className="font-serif text-[40px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to transform your <span className="text-accent-teal italic">workflow</span>?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            See all these features in action with a personalised demo tailored to your practice.
          </p>
          <Button href="/book-demo" size="lg">
            Book a Demo
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      </section>
    </>
  )
}
